import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, FileText, User, Calendar, Edit } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOPDetail = ({ user }) => {
  const { sopId } = useParams();
  const navigate = useNavigate();
  const [sop, setSop] = useState(null);
  const [assignedUser, setAssignedUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSOP();
  }, [sopId]);

  const fetchSOP = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch the specific SOP
      const sopResponse = await axios.get(`${API_URL}/sops`, { headers });
      const foundSop = sopResponse.data.find(s => s.id === sopId);
      
      if (!foundSop) {
        toast.error('SOP not found');
        navigate('/dashboard/sops');
        return;
      }
      
      setSop(foundSop);
      
      // Fetch assigned user details
      if (foundSop.assigned_to) {
        const usersResponse = await axios.get(`${API_URL}/users`, { headers });
        const foundUser = usersResponse.data.find(u => u.id === foundSop.assigned_to);
        setAssignedUser(foundUser);
      }
    } catch (error) {
      toast.error('Failed to fetch SOP details');
      navigate('/dashboard/sops');
    } finally {
      setLoading(false);
    }
  };

  // Check if user can edit this SOP (not if they are the assignee)
  const canEditSOP = () => {
    if (!sop || !user) return false;
    // Assignees cannot edit - only view
    if (sop.assigned_to === user.id) return false;
    // Team members cannot edit
    if (user.role === 'team_member') return false;
    // Superadmin, CEO, Manager, Team Lead can edit
    return ['superadmin', 'ceo', 'manager', 'team_lead'].includes(user.role);
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <p>Loading SOP details...</p>
      </div>
    );
  }

  if (!sop) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <p>SOP not found</p>
      </div>
    );
  }

  return (
    <div className="sop-detail-page" style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 20px',
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            color: 'var(--text-primary)',
            transition: 'all 0.2s'
          }}
        >
          <ArrowLeft size={18} />
          Back
        </button>

        {canEditSOP() && (
          <button
            onClick={() => navigate('/dashboard/sops')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              background: '#241665',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#1a1050'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#241665'}
          >
            <Edit size={18} />
            Edit SOP
          </button>
        )}
      </div>

      {/* SOP Content Card */}
      <div style={{
        background: 'var(--bg-primary)',
        borderRadius: '12px',
        padding: '32px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: '1px solid var(--border-color)'
      }}>
        {/* Title Section */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'start', 
          gap: '16px',
          marginBottom: '24px',
          paddingBottom: '24px',
          borderBottom: '2px solid var(--border-color)'
        }}>
          <div style={{
            padding: '12px',
            background: '#241665',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <FileText size={32} color="white" />
          </div>
          <div style={{ flex: 1 }}>
            <h1 style={{ 
              margin: '0 0 8px 0', 
              fontSize: '28px',
              fontWeight: '600',
              color: 'var(--text-primary)',
              lineHeight: '1.3'
            }}>
              {sop.title}
            </h1>
            {sop.department && (
              <span style={{
                display: 'inline-block',
                padding: '4px 12px',
                background: '#24166515',
                color: '#241665',
                borderRadius: '12px',
                fontSize: '13px',
                fontWeight: '600'
              }}>
                {sop.department}
              </span>
            )}
          </div>
        </div>

        {/* Meta Information */}
        <div style={{
          display: 'flex',
          gap: '32px',
          marginBottom: '32px',
          padding: '16px',
          background: 'var(--bg-secondary)',
          borderRadius: '8px',
          flexWrap: 'wrap'
        }}>
          {assignedUser && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <User size={18} color="#241665" />
              <div>
                <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '2px' }}>Assigned To</div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
                  {assignedUser.full_name}
                </div>
              </div>
            </div>
          )}
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Calendar size={18} color="#241665" />
            <div>
              <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '2px' }}>Created</div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
                {new Date(sop.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Description/Content */}
        <div>
          <h3 style={{ 
            fontSize: '18px', 
            fontWeight: '600', 
            color: 'var(--text-primary)',
            marginBottom: '16px'
          }}>
            Description
          </h3>
          <div 
            style={{
              fontSize: '15px',
              lineHeight: '1.8',
              color: 'var(--text-secondary)',
              wordWrap: 'break-word'
            }}
            dangerouslySetInnerHTML={{ __html: sop.description }}
          />
        </div>
      </div>
    </div>
  );
};

export default SOPDetail;
